import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    IconButton, 
    Chip, 
    CircularProgress,
    Alert,
    Tooltip,
    Card,
    CardContent,
    Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VerifiedIcon from '@mui/icons-material/Verified';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TelegramIcon from '@mui/icons-material/Telegram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PhoneIcon from '@mui/icons-material/Phone';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { communicationChannelsApi } from '../../api/communicationChannelsApi';
import { 
    CommunicationChannel, 
    CommunicationChannelType 
} from '../../types/communication-channel';
import CommunicationChannelDialog from './CommunicationChannelDialog';

interface CommunicationChannelsListProps {
    workerId: string;
}

// Icon mapping for each channel type
const ChannelIcons: Record<CommunicationChannelType, React.ElementType> = {
    [CommunicationChannelType.WHATSAPP]: WhatsAppIcon,
    [CommunicationChannelType.EMAIL]: EmailIcon,
    [CommunicationChannelType.FACEBOOK]: FacebookIcon,
    [CommunicationChannelType.INSTAGRAM]: InstagramIcon,
    [CommunicationChannelType.TELEGRAM]: TelegramIcon,
    [CommunicationChannelType.LINKEDIN]: LinkedInIcon,
    [CommunicationChannelType.PHONE]: PhoneIcon,
    [CommunicationChannelType.SKYPE]: VideoCallIcon,
    [CommunicationChannelType.OTHER]: AlternateEmailIcon,
};

// Brand colors for each channel type
const ChannelColors: Record<CommunicationChannelType, { bg: string; color: string; iconColor: string }> = {
    [CommunicationChannelType.WHATSAPP]: { bg: '#e7f7e4', color: '#25D366', iconColor: '#25D366' },
    [CommunicationChannelType.EMAIL]: { bg: '#e3f2fd', color: '#1976d2', iconColor: '#1976d2' },
    [CommunicationChannelType.FACEBOOK]: { bg: '#e8f0fe', color: '#1877F2', iconColor: '#1877F2' },
    [CommunicationChannelType.INSTAGRAM]: { bg: '#fce4ec', color: '#E4405F', iconColor: '#E4405F' },
    [CommunicationChannelType.TELEGRAM]: { bg: '#e1f5fe', color: '#0088cc', iconColor: '#0088cc' },
    [CommunicationChannelType.LINKEDIN]: { bg: '#e3f2fd', color: '#0A66C2', iconColor: '#0A66C2' },
    [CommunicationChannelType.PHONE]: { bg: '#e8f5e9', color: '#4caf50', iconColor: '#4caf50' },
    [CommunicationChannelType.SKYPE]: { bg: '#e3f2fd', color: '#00AFF0', iconColor: '#00AFF0' },
    [CommunicationChannelType.OTHER]: { bg: '#f5f5f5', color: '#757575', iconColor: '#757575' },
};

// Generate direct link for each channel type
const getChannelLink = (channel: CommunicationChannel): string | null => {
    const value = channel.channelValue;
    
    switch (channel.channelType) {
        case CommunicationChannelType.WHATSAPP:
            // Remove non-numeric characters and add WhatsApp web link
            const phoneNumber = value.replace(/\D/g, '');
            return `https://wa.me/${phoneNumber}`;
            
        case CommunicationChannelType.EMAIL:
            return `mailto:${value}`;
            
        case CommunicationChannelType.PHONE:
            return `tel:${value}`;
            
        case CommunicationChannelType.FACEBOOK:
            // If it's already a URL, use it; otherwise construct Facebook URL
            if (value.startsWith('http')) {
                return value;
            }
            return `https://facebook.com/${value}`;
            
        case CommunicationChannelType.INSTAGRAM:
            // If it's already a URL, use it; otherwise construct Instagram URL
            if (value.startsWith('http')) {
                return value;
            }
            const igUsername = value.replace('@', '');
            return `https://instagram.com/${igUsername}`;
            
        case CommunicationChannelType.LINKEDIN:
            // If it's already a URL, use it; otherwise construct LinkedIn URL
            if (value.startsWith('http')) {
                return value;
            }
            return `https://linkedin.com/in/${value}`;
            
        case CommunicationChannelType.TELEGRAM:
            // If it's a phone number, use the phone link; otherwise use username
            if (value.match(/^\+?\d+$/)) {
                const phone = value.replace(/\D/g, '');
                return `https://t.me/+${phone}`;
            }
            const tgUsername = value.replace('@', '');
            return `https://t.me/${tgUsername}`;
            
        case CommunicationChannelType.SKYPE:
            // Skype URI scheme
            if (value.match(/^\+?\d+$/)) {
                return `skype:${value}?call`;
            }
            return `skype:${value}?chat`;
            
        default:
            // For OTHER type, try to detect if it's a URL
            if (value.startsWith('http')) {
                return value;
            }
            return null;
    }
};

const CommunicationChannelsList: React.FC<CommunicationChannelsListProps> = ({ workerId }) => {
    const [channels, setChannels] = useState<CommunicationChannel[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingChannel, setEditingChannel] = useState<CommunicationChannel | null>(null);

    useEffect(() => {
        loadChannels();
    }, [workerId]);

    const loadChannels = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await communicationChannelsApi.getWorkerChannels(workerId);
            setChannels(data);
        } catch (err) {
            setError('Failed to load communication channels');
            console.error('Error loading channels:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddClick = () => {
        setEditingChannel(null);
        setDialogOpen(true);
    };

    const handleEditClick = (channel: CommunicationChannel) => {
        setEditingChannel(channel);
        setDialogOpen(true);
    };

    const handleDeleteClick = async (channelId: number) => {
        if (!confirm('Are you sure you want to delete this communication channel?')) {
            return;
        }

        try {
            await communicationChannelsApi.deleteChannel(channelId);
            await loadChannels();
        } catch (err) {
            console.error('Error deleting channel:', err);
            setError('Failed to delete communication channel');
        }
    };

    const handleSetPrimary = async (channelId: number) => {
        try {
            await communicationChannelsApi.setPrimaryChannel(channelId);
            await loadChannels();
        } catch (err) {
            console.error('Error setting primary channel:', err);
            setError('Failed to set primary channel');
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditingChannel(null);
    };

    const handleDialogSave = async () => {
        await loadChannels();
        handleDialogClose();
    };

    const getChannelDisplay = (channel: CommunicationChannel): string => {
        if (channel.label) {
            return `${channel.channelValue} (${channel.label})`;
        }
        return channel.channelValue;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Communication Channels</Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                        size="small"
                    >
                        Add
                    </Button>
                </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {channels.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No channels added yet
                </Typography>
            ) : (
                <Stack spacing={0.5}>
                    {channels.map((channel) => {
                        const Icon = ChannelIcons[channel.channelType];
                        const colors = ChannelColors[channel.channelType];
                        
                        return (
                            <Box 
                                key={channel.id}
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    p: 1,
                                    borderRadius: 2,
                                    backgroundColor: colors.bg,
                                    border: `1px solid`,
                                    borderColor: `${colors.color}30`,
                                    '&:hover': { 
                                        backgroundColor: colors.bg,
                                        borderColor: colors.color,
                                        boxShadow: `0 0 0 1px ${colors.color}30`
                                    }
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1} flex={1}>
                                    <Icon sx={{ fontSize: 20, color: colors.iconColor }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {getChannelDisplay(channel)}
                                    </Typography>
                                    {channel.isPrimary && (
                                        <Chip 
                                            icon={<StarIcon sx={{ fontSize: 14 }} />}
                                            label="Primary" 
                                            size="small" 
                                            sx={{ 
                                                height: 22, 
                                                fontSize: '0.7rem',
                                                backgroundColor: '#fff',
                                                color: colors.color,
                                                borderColor: colors.color,
                                                '& .MuiChip-icon': {
                                                    color: colors.color
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    )}
                                    {channel.isVerified && (
                                        <Tooltip title="Verified">
                                            <VerifiedIcon sx={{ fontSize: 16, color: colors.iconColor }} />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Box display="flex" gap={0.5}>
                                    {(() => {
                                        const link = getChannelLink(channel);
                                        return link ? (
                                            <Tooltip title="Open">
                                                <IconButton
                                                    component="a"
                                                    href={link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="small"
                                                    sx={{ color: colors.iconColor }}
                                                >
                                                    <OpenInNewIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null;
                                    })()}
                                    {!channel.isPrimary && (
                                        <Tooltip title="Set as Primary">
                                            <IconButton
                                                onClick={() => handleSetPrimary(channel.id)}
                                                size="small"
                                            >
                                                <StarBorderIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Edit">
                                        <IconButton
                                            onClick={() => handleEditClick(channel)}
                                            size="small"
                                        >
                                            <EditIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            onClick={() => handleDeleteClick(channel.id)}
                                            size="small"
                                        >
                                            <DeleteIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
            )}

            {dialogOpen && (
                <CommunicationChannelDialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                    onSave={handleDialogSave}
                    workerId={workerId}
                    channel={editingChannel}
                />
            )}
            </CardContent>
        </Card>
    );
};

export default CommunicationChannelsList;